import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CodeBlock, DocsToc } from "../components";
import { DocsTocItem } from "../interfaces";
import {
  DEFAULT_TOKEN_TTL_SECONDS,
  MAX_DATE_RANGE_DAYS,
  QUICKSTART_ENVIRONMENT_SNIPPET,
  QUICKSTART_EVENTS_SNIPPET,
  QUICKSTART_SALES_SNIPPET,
  QUICKSTART_TOKEN_SNIPPET,
  QUICKSTART_VENUES_SNIPPET,
} from "../utils";

interface QuickstartStep {
  key: string;
  snippet: string | null;
}

const STEPS: QuickstartStep[] = [
  { key: "credentials", snippet: null },
  { key: "environment", snippet: QUICKSTART_ENVIRONMENT_SNIPPET },
  { key: "token", snippet: QUICKSTART_TOKEN_SNIPPET },
  { key: "venues", snippet: QUICKSTART_VENUES_SNIPPET },
  { key: "events", snippet: QUICKSTART_EVENTS_SNIPPET },
  { key: "sales", snippet: QUICKSTART_SALES_SNIPPET },
];

const TOC_ITEMS: DocsTocItem[] = [
  ...STEPS.map((step): DocsTocItem => {
    return {
      id: `quickstart-${step.key}`,
      translationKey: `docs.quickstart.steps.${step.key}.title`,
      level: 1,
    };
  }),
  {
    id: "quickstart-remember",
    translationKey: "docs.quickstart.remember.title",
    level: 1,
  },
];

export const Quickstart = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col-reverse gap-8 lg:flex-row lg:gap-12">
      <div className="min-w-0 flex-1 space-y-12">
        <div className="space-y-4">
          <h2 className="text-xl font-bold lg:text-2xl">
            {t("docs.quickstart.title")}
          </h2>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.quickstart.intro")}
          </p>
        </div>

        <ol className="space-y-10">
          {STEPS.map((step, index) => (
            <li key={step.key} className="space-y-4">
              <h3
                id={`quickstart-${step.key}`}
                className="scroll-mt-6 text-lg font-bold lg:text-xl"
              >
                <span className="text-primary">{index + 1}. </span>
                {t(`docs.quickstart.steps.${step.key}.title`)}
              </h3>
              <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
                {t(`docs.quickstart.steps.${step.key}.content`)}
              </p>
              {step.snippet !== null && (
                <CodeBlock
                  code={step.snippet}
                  language="bash"
                  syntax="bash"
                />
              )}
            </li>
          ))}
        </ol>

        <div
          id="quickstart-remember"
          className="scroll-mt-6 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-5"
        >
          <h3 className="text-base font-bold">
            {t("docs.quickstart.remember.title")}
          </h3>
          <ul className="list-outside list-disc space-y-2 pl-5 text-gray-400">
            <li className="text-base/relaxed">
              {t("docs.quickstart.remember.token", {
                seconds: DEFAULT_TOKEN_TTL_SECONDS,
              })}
            </li>
            <li className="text-base/relaxed">
              {t("docs.quickstart.remember.closedEvents")}
            </li>
            <li className="text-base/relaxed">
              {t("docs.quickstart.remember.dateRange", {
                days: MAX_DATE_RANGE_DAYS,
              })}
            </li>
            <li className="text-base/relaxed">
              {t("docs.quickstart.remember.money")}
            </li>
            {/* YOSHI-10464: the two traps that used to live in the billing guide. They decide whether
                an invoice reconciles, so they stay even though the guide is gone. */}
            <li className="text-base/relaxed">
              {t("docs.quickstart.remember.tip")}
            </li>
            <li className="text-base/relaxed">
              {t("docs.quickstart.remember.code")}
            </li>
          </ul>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-bold lg:text-xl">
            {t("docs.quickstart.nextTitle")}
          </h3>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.quickstart.nextContent")}
          </p>
          <Link
            id="docs-quickstart-endpoints"
            to="/docs/endpoints"
            className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            {t("docs.nav.endpoints")}
          </Link>
        </div>
      </div>
      <DocsToc items={TOC_ITEMS} />
    </div>
  );
};
